import React, { useState, useEffect, useCallback } from 'react';
import defaultLogo from '../assets/logo.svg';
import backbaseLogo from '../assets/backbaselogo.svg';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose }) => {
  const [isSaved, setIsSaved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [selectedLogo, setSelectedLogo] = useState<'default' | 'backbase'>('default');
  
  // Feature flag to show/hide Backbase logo option
  const showBackbaseOption = false; // Set to true to re-enable Backbase logo option

  // Load settings function
  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      // Load logo preference from localStorage
      const savedLogo = localStorage.getItem('logo_preference') || 'default';
      
      // If Backbase option is hidden but was previously selected, reset to default
      if (!showBackbaseOption && savedLogo === 'backbase') {
        setSelectedLogo('default');
        localStorage.setItem('logo_preference', 'default');
        // Dispatch event to update logo in other components
        window.dispatchEvent(new CustomEvent('logo-changed', { 
          detail: { logo: 'default' } 
        }));
      } else {
        setSelectedLogo(savedLogo as 'default' | 'backbase');
      }
      
      setIsSaved(false);
      setSaveError(null);
    } catch (error) {
      console.error('Error loading settings:', error);
      setSaveError('Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  }, [showBackbaseOption]);

  useEffect(() => {
    // Load saved settings when modal opens
    if (isOpen) {
      loadSettings();
    }
  }, [isOpen, loadSettings]);

  const handleSaveSettings = async () => {
    setIsLoading(true);
    try {
      // Save settings to localStorage
      let settingsSaved = true;
      try {
        // Save logo preference
        localStorage.setItem('logo_preference', selectedLogo);
        
        // Verify settings were saved
        const savedLogo = localStorage.getItem('logo_preference') === selectedLogo;
        settingsSaved = savedLogo;
        
        // Dispatch an event to notify components that use the logo
        window.dispatchEvent(new CustomEvent('logo-changed', { 
          detail: { logo: selectedLogo } 
        }));
      } catch (error) {
        console.error('Error saving settings:', error);
        settingsSaved = false;
      }
      
      // Check if saving was successful
      if (!settingsSaved) {
        console.error('Settings save failed');
        setSaveError('Failed to save settings. Please try again.');
        setIsLoading(false);
        return;
      }
      
      // Show success message
      setSaveError(null);
      setIsSaved(true);
      
      setTimeout(() => setIsSaved(false), 2000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveError('Failed to save settings');
    } finally {
      setIsLoading(false);
    }
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-2 flex items-center justify-between">
                  <span>Global Settings</span>
                  {isSaved && (
                    <span className="text-sm text-green-600 animate-pulse ml-4">
                      ✓ Settings saved!
                    </span>
                  )}
                </h3>
                
                {saveError && (
                  <div className="mb-4 p-2 bg-red-100 border border-red-400 text-red-700 rounded">
                    {saveError}
                    <button 
                      className="ml-2 text-red-700 hover:text-red-900" 
                      onClick={() => setSaveError(null)}
                    >
                      ×
                    </button>
                  </div>
                )}
                
                <div className="mt-6">
                  <h4 className="font-medium text-gray-700 mb-3">Application Settings</h4>
                  
                  <div className="mb-4">
                    <label htmlFor="logo-selection" className="block text-sm font-medium text-gray-700 mb-1">
                      Application Logo
                    </label>
                    <div className="mt-2 flex flex-col space-y-3">
                      <div className="flex items-center">
                        <input
                          id="default-logo"
                          name="logo-selection"
                          type="radio"
                          value="default"
                          checked={selectedLogo === 'default'}
                          onChange={() => setSelectedLogo('default')}
                          className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                        />
                        <label htmlFor="default-logo" className="ml-3 flex items-center">
                          <span className="block text-sm font-medium text-gray-700 mr-3">Default Logo</span>
                          <img src={defaultLogo} alt="Default Logo" className="h-8 w-auto" />
                        </label>
                      </div>
                      
                      {showBackbaseOption && (
                        <div className="flex items-center">
                          <input
                            id="backbase-logo"
                            name="logo-selection"
                            type="radio"
                            value="backbase"
                            checked={selectedLogo === 'backbase'}
                            onChange={() => setSelectedLogo('backbase')}
                            className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                          />
                          <label htmlFor="backbase-logo" className="ml-3 flex items-center">
                            <span className="block text-sm font-medium text-gray-700 mr-3">Backbase Logo</span>
                            <img src={backbaseLogo} alt="Backbase Logo" className="h-8 w-auto" />
                          </label>
                        </div>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Choose the logo displayed in the application header and navigation.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:bg-blue-300"
              onClick={handleSaveSettings}
              disabled={isLoading || (selectedLogo === (localStorage.getItem('logo_preference') || 'default'))}
            >
              {isLoading ? 'Saving...' : 'Save Settings'}
            </button>
            <button
              type="button"
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
              onClick={onClose}
              disabled={isLoading}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}; 